import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Button,
    Center,
    Flex,
    Grid,
    HStack,
    VStack,
    Text,
    Input,
    Select,
    Tag,
    Table,
    TableContainer,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    MenuDivider,
    IconButton,
    useToast,
    Spinner,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
} from '@chakra-ui/react';
import {
    FiPlus,
    FiSearch,
    FiMoreVertical,
    FiPlay,
    FiPause,
    FiCopy,
    FiTrash2,
    FiEdit,
    FiBarChart2,
    FiUsers,
    FiActivity,
    FiClock,
    FiLayers,
} from 'react-icons/fi';
import { LuGauge } from 'react-icons/lu';
import Header from '../../components/Header';
import PageHeading from '../../components/shared/PageHeading';
import BoxWrapper from '../../components/BoxWrapper/BoxWrapper';
import { useNavigate } from 'react-router-dom';
import API from '../../helpers/axiosInstance';
import { getApiHeaders } from '../../utils/DefaultFunction';
import usePagePermissions from '../../hooks/usePagePermissions';

const getCustomerId = () => {
    try { return JSON.parse(localStorage.getItem('customer'))?.customer_id; } catch { return null; }
};

const getRfmHeaders = () => ({
    ...getApiHeaders(),
    'x-customer-id': getCustomerId(),
});

const RFMModels = ({ header }) => {
    const navigate = useNavigate();
    const { loading: featurePermissionsLoading, can } = usePagePermissions('marketing/rfm-models');

    const rfmModelListPermission = can("rfm-model-list");
    // Both Create and Edit route into the same configure form, so they share one slug.
    const rfmModelConfigurePermission = can("rfm-model-configure");
    const rfmModelDeletePermission = can("rfm-model-delete");
    const rfmModelDashboardPermission = can("rfm-model-dashboard");

    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const toast = useToast();
    const [models, setModels] = useState([]);
    const [stats, setStats] = useState(null);
    const [listLoading, setListLoading] = useState(true);
    // Row whose clone is in flight, the model awaiting delete confirmation, and whether
    // that delete request is running.
    const [busyId, setBusyId] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    // AlertDialog needs a focus target that is NOT the destructive button.
    const cancelDeleteRef = useRef(null);

    // Hoisted out of the effect so clone/delete can refetch after mutating.
    const fetchModels = useCallback(async () => {
        setListLoading(true);
        try {
            const res = await API.get(
                `${process.env.REACT_APP_API_URL}/rfm/config`,
                { headers: getRfmHeaders() }
            );
            if (res.data?.status) {
                const raw = res.data?.data?.event_configs || [];
                setModels(raw);
                setStats(res.data?.data?.stats || null);
            }
        } catch (e) {
            console.error('Failed to fetch RFM models:', e);
        } finally {
            setListLoading(false);
        }
    }, []);

    useEffect(() => {
        if (featurePermissionsLoading || !can('rfm-model-list')) return;
        fetchModels();
    }, [featurePermissionsLoading, can, fetchModels]);

    const getStatusStyle = (status) => {
        switch (status) {
            case 'active': return { bg: '#dcfce7', color: '#16a34a' };
            case 'paused': return { bg: '#fee2e2', color: '#dc2626' };
            default:       return { bg: '#f1f5f9', color: '#64748b' };
        }
    };

    /**
     * Distinct segment names across all models (RFM_007).
     *
     * Derived from the data rather than hardcoded to 4, so it stays correct if a model ever
     * carries a different set. Falls back to the four fixed names when nothing has loaded.
     */
    const distinctSegmentCount = React.useMemo(() => {
        const names = new Set();
        models.forEach(m => (m.segments || []).forEach(s => s?.name && names.add(s.name)));
        return names.size || 4;
    }, [models]);

    const filteredModels = models.filter((model) => {
        const name = model.name || '';
        const matchesSearch = name.toLowerCase().includes(searchQuery.toLowerCase());
        const status = model.is_enabled ? 'active' : 'paused';
        const matchesStatus = statusFilter === 'all' || status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const handleRunNow = (model) => {
        toast({
            title: 'Running Model',
            description: `"${model.name}" has been queued for execution.`,
            status: 'success',
            duration: 3000,
            isClosable: true,
        });
    };

    /**
     * RFM_003 — Clone.
     *
     * Was a bare toast: it announced "Model Cloned" and did nothing. There is no clone
     * endpoint, so this composes the ones that exist — read the source config, POST a new
     * event-config under a "(Copy)" name, then replay the rule / score / segment PUTs
     * against the new id. Each step is awaited, and the toast only fires once the copy
     * actually exists and the list has been refetched.
     */
    const handleClone = async (model) => {
        setBusyId(model._id);
        try {
            // Re-read by id: the list response may omit nested config the copy needs.
            const res = await API.get(
                `${process.env.REACT_APP_API_URL}/rfm/config?_id=${model._id}`,
                { headers: getRfmHeaders() }
            );
            const src = res.data?.data?.event_config || (res.data?.data?.event_configs || [])[0];
            if (!res.data?.status || !src) throw new Error('Could not read the source model');

            const { _id, created_at, updated_at, ...rest } = src;
            const created = await API.post(
                `${process.env.REACT_APP_API_URL}/rfm/create-event-config`,
                { ...rest, name: `${src.name} (Copy)`, is_enabled: false },
                { headers: getRfmHeaders() }
            );
            const newId = created.data?.data?.event_config?._id
                || created.data?.data?._id
                || created.data?.data?.config?._id;
            if (!created.data?.status || !newId) throw new Error('Clone was not created');

            // Best-effort for the dependent sections: the copy already exists, so a failure
            // here should not read as "nothing happened".
            const carry = [
                ['rule_config',    { recency: src.recency, frequency: src.frequency, monetary: src.monetary }],
                ['score_config',   { recency: src.recency, frequency: src.frequency, monetary: src.monetary }],
                ['segment_config', { segments: src.segments }],
            ];
            for (const [path, body] of carry) {
                try {
                    await API.put(
                        `${process.env.REACT_APP_API_URL}/rfm/${path}`,
                        { ...body, _id: newId },
                        { headers: getRfmHeaders() }
                    );
                } catch (e) {
                    console.error(`Clone: failed to copy ${path}`, e);
                }
            }

            await fetchModels();
            toast({
                title: 'Model cloned',
                description: `Created "${src.name} (Copy)" — it starts paused so it will not run until you enable it.`,
                status: 'success', duration: 4000, isClosable: true,
            });
        } catch (err) {
            toast({
                title: 'Clone failed',
                description: err?.response?.data?.message || err?.message || 'Request failed',
                status: 'error', duration: 4000, isClosable: true,
            });
        } finally {
            setBusyId(null);
        }
    };

    /**
     * RFM_002 — Delete.
     *
     * Was a bare toast too: no confirmation, no request, and the row stayed. Now the menu
     * item only opens the confirmation dialog; the request lives in confirmDelete so the
     * success toast cannot fire before the server has agreed.
     */
    const handleDelete = (model) => setPendingDelete(model);

    const confirmDelete = async () => {
        const model = pendingDelete;
        if (!model) return;
        setDeleting(true);
        try {
            // `_id` is sent as BOTH a query param and a request body, because the sibling
            // endpoints disagree: create/update-event-config take it in the body, while the
            // GET reads ?_id=. Sending both means this works either way — trim to whichever
            // one the handler actually reads once that is confirmed.
            await API.delete(
                `${process.env.REACT_APP_API_URL}/rfm/delete-event-config?_id=${model._id}`,
                { headers: getRfmHeaders(), data: { _id: model._id } }
            );
            setPendingDelete(null);
            await fetchModels();
            toast({
                title: 'Model deleted',
                description: `"${model.name}" has been deleted.`,
                status: 'success', duration: 3000, isClosable: true,
            });
        } catch (err) {
            toast({
                title: 'Delete failed',
                description: err?.response?.data?.message || err?.message || 'Request failed',
                status: 'error', duration: 5000, isClosable: true,
            });
        } finally {
            setDeleting(false);
        }
    };

    if (featurePermissionsLoading) {
        return (
            <>
                <Header header={header} />
                <BoxWrapper>
                    <Center h="400px">
                        <Spinner size="xl" color="blue.500" thickness="4px" />
                    </Center>
                </BoxWrapper>
            </>
        );
    }

    return (
        <>
            <Header header={header} />
            <BoxWrapper>
              {rfmModelListPermission ? (
                <>
                {/* Page Header */}
                <PageHeading icon={LuGauge} title="RFM Models" subtitle="Build behavioral intelligence models using Recency, Frequency, and Monetary event analytics" mb={8}>
                    <HStack>
                        {rfmModelConfigurePermission && (
                            <>
                                <Button leftIcon={<FiLayers />} variant="outline" size="sm">
                                    Create From Template
                                </Button>
                                <Button leftIcon={<FiPlus />} bg="var(--brand-primary)" color="white" _hover={{ bg: 'var(--brand-primary-hover)' }} size="sm" onClick={() => navigate('/marketing/rfm-models/configure/new')}>
                                    Create New Model
                                </Button>
                            </>
                        )}
                    </HStack>
                </PageHeading>

                {/* KPI Cards */}
                <Grid
                    templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(5, 1fr)' }}
                    gap={4}
                    mb={8}
                >
                    {[
                        {
                            label: 'Total Models',
                            value: stats?.total_model ?? '—',
                            sub: 'All configured models',
                            icon: <FiLayers />,
                            iconBg: 'blue.50',
                            iconColor: 'blue.500',
                        },
                        {
                            label: 'Active Models',
                            value: stats?.active_model ?? '—',
                            sub: 'Currently enabled',
                            icon: <FiActivity />,
                            iconBg: 'green.50',
                            iconColor: 'green.500',
                        },
                        {
                            label: 'Processed Today',
                            value: stats?.process_today ?? '—',
                            sub: `Yesterday: ${stats?.process_yesterday ?? '—'}`,
                            icon: <FiUsers />,
                            iconBg: 'purple.50',
                            iconColor: 'purple.500',
                        },
                        {
                            label: 'Processed Yesterday',
                            value: stats?.process_yesterday ?? '—',
                            sub: 'Previous day run',
                            icon: <FiClock />,
                            iconBg: 'orange.50',
                            iconColor: 'orange.500',
                        },
                        {
                            // RFM_007: this showed stats.total_segment — a running SUM that
                            // grows by 4 with every model, because every model carries the same
                            // four fixed segments (Champions / Loyal / At Risk / Lost). "9
                            // segments" across 2 models told the operator nothing. Show the
                            // distinct segment count instead, with the total as the sub-line
                            // where its meaning is stated rather than implied.
                            label: 'Segments per Model',
                            value: distinctSegmentCount,
                            sub: stats?.total_segment != null
                                ? `${stats.total_segment} segment assignments across ${models.length} model${models.length === 1 ? '' : 's'}`
                                : 'Champions, Loyal, At Risk, Lost',
                            icon: <FiBarChart2 />,
                            iconBg: 'pink.50',
                            iconColor: 'pink.500',
                        },
                    ].map((card) => (
                        <Box key={card.label} className="box-style-card">
                            <Flex justify="space-between" align="flex-start">
                                <Box>
                                    <Text fontSize="xs" color="gray.500" fontWeight="500" mb={1}>{card.label}</Text>
                                    <Text fontSize="2xl" fontWeight="700" color="gray.800" lineHeight="1">
                                        {/* toLocaleString only on numbers — the '—' placeholder is a string. */}
                                        {listLoading ? '—' : (typeof card.value === 'number' ? card.value.toLocaleString() : card.value)}
                                    </Text>
                                    <Text fontSize="xs" color="gray.400" mt={1}>{card.sub}</Text>
                                </Box>
                                <Flex
                                    align="center"
                                    justify="center"
                                    w="38px"
                                    h="38px"
                                    borderRadius="lg"
                                    bg={card.iconBg}
                                    color={card.iconColor}
                                    fontSize="lg"
                                    flexShrink={0}
                                >
                                    {card.icon}
                                </Flex>
                            </Flex>
                        </Box>
                    ))}
                </Grid>

                {/* Filters & Table */}
                <Box className="box-style-card">
                    <VStack align="start" spacing={0} mb={4}>
                        <Text fontSize="xl" fontWeight={600}>Models</Text>
                        <Text color="gray.600">Manage and monitor your RFM behavioral models</Text>
                    </VStack>

                    {/* Search & Filters */}
                    <Grid templateColumns={{ base: '1fr', md: '2fr 1fr 1fr' }} gap={4} mb={6}>
                        <Box position="relative">
                            <Input
                                placeholder="Search models..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                pl={10}
                            />
                            <Box
                                position="absolute"
                                left={3}
                                top="50%"
                                transform="translateY(-50%)"
                                color="gray.400"
                                pointerEvents="none"
                            >
                                <FiSearch />
                            </Box>
                        </Box>

                        <Select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="paused">Paused</option>
                            <option value="draft">Draft</option>
                        </Select>

                        <Select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                        >
                            <option value="all">All Categories</option>
                            <option value="Customer Value">Customer Value</option>
                            <option value="Digital Adoption">Digital Adoption</option>
                            <option value="Cards">Cards</option>
                            <option value="Lending">Lending</option>
                            <option value="Churn Prevention">Churn Prevention</option>
                            <option value="Wealth Management">Wealth Management</option>
                            <option value="Payroll Banking">Payroll Banking</option>
                            <option value="Channel Migration">Channel Migration</option>
                            <option value="Islamic Banking">Islamic Banking</option>
                        </Select>
                    </Grid>

                    {/* Table */}
                    <TableContainer>
                        <Table variant="simple">
                            <Thead>
                                <Tr>
                                    <Th>Model Name</Th>
                                    <Th>Status</Th>
                                    <Th>Frequency</Th>
                                    <Th>Segments</Th>
                                    <Th>Created At</Th>
                                    <Th textAlign="center">Actions</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {listLoading ? (
                                    <Tr><Td colSpan={6} textAlign="center" py={10}><Text color="gray.400">Loading...</Text></Td></Tr>
                                ) : filteredModels.map((model) => {
                                    const status = model.is_enabled ? 'active' : 'paused';
                                    return (
                                    <Tr key={model._id} _hover={{ bg: 'gray.50' }}>
                                        <Td>
                                            <Text fontWeight="semibold">{model.name}</Text>
                                        </Td>
                                        <Td>
                                            <Tag size="sm" borderRadius="full" {...getStatusStyle(status)}>
                                                {status.charAt(0).toUpperCase() + status.slice(1)}
                                            </Tag>
                                        </Td>
                                        <Td>
                                            <HStack spacing={1} fontSize="sm">
                                                <FiClock />
                                                <Text>{model.refresh_frequency || '—'}</Text>
                                            </HStack>
                                        </Td>
                                        <Td>
                                            <Text fontSize="sm">{model.segments?.length ?? 0}</Text>
                                        </Td>
                                        <Td>
                                            <Text fontSize="sm" color="gray.600">
                                                {model.created_at ? new Date(model.created_at).toLocaleDateString() : '—'}
                                            </Text>
                                        </Td>
                                        <Td textAlign="center">
                                            <Menu>
                                                <MenuButton
                                                    as={IconButton}
                                                    icon={<FiMoreVertical />}
                                                    variant="ghost"
                                                    size="sm"
                                                    aria-label="Actions"
                                                />
                                                <MenuList>
                                                    {rfmModelConfigurePermission && (
                                                        <MenuItem icon={<FiEdit />} onClick={() => navigate(`/marketing/rfm-models/configure/${model._id}`)}>Edit</MenuItem>
                                                    )}
                                                    {rfmModelDashboardPermission && (
                                                        <MenuItem icon={<FiBarChart2 />} onClick={() => navigate(`/marketing/rfm-models/dashboard/${model._id}`)}>View Analytics</MenuItem>
                                                    )}
                                                    {/* Run Now and Pause stay disabled: both are still toast-only stubs
                                                        (no /rfm/run wiring, no pause handler at all) and were not part of
                                                        the reported bugs. Clone is live again now that RFM_003 actually
                                                        performs the copy. */}
                                                    {/* <MenuItem icon={<FiPlay />} onClick={() => handleRunNow(model)}>Run Now</MenuItem> */}
                                                    {rfmModelConfigurePermission && (
                                                        <MenuItem
                                                            icon={<FiCopy />}
                                                            isDisabled={busyId === model._id}
                                                            closeOnSelect={false}
                                                            onClick={() => handleClone(model)}
                                                        >
                                                            {busyId === model._id ? 'Cloning…' : 'Clone'}
                                                        </MenuItem>
                                                    )}
                                                    {/* <MenuItem icon={<FiPause />}>Pause</MenuItem> */}
                                                    <MenuDivider />
                                                    {rfmModelDeletePermission && (
                                                        <MenuItem icon={<FiTrash2 />} color="red.500" onClick={() => handleDelete(model)}>Delete</MenuItem>
                                                    )}
                                                </MenuList>
                                            </Menu>
                                        </Td>
                                    </Tr>
                                    );
                                })}
                            </Tbody>
                        </Table>
                    </TableContainer>

                    {filteredModels.length === 0 && (
                        <Box textAlign="center" py={10}>
                            <Text color="gray.500">No models found matching your criteria.</Text>
                            <Button
                                mt={4}
                                variant="outline"
                                onClick={() => {
                                    setSearchQuery('');
                                    setStatusFilter('all');
                                    setCategoryFilter('all');
                                }}
                            >
                                Clear Filters
                            </Button>
                        </Box>
                    )}
                </Box>

                {/* RFM_002: delete now requires confirmation, and the request only runs from
                    here — so the success toast can never precede the server's agreement. */}
                <AlertDialog
                    isOpen={!!pendingDelete}
                    leastDestructiveRef={cancelDeleteRef}
                    onClose={() => setPendingDelete(null)}
                    isCentered
                >
                    <AlertDialogOverlay>
                        <AlertDialogContent>
                            <AlertDialogHeader fontSize="lg" fontWeight="bold">Delete RFM model</AlertDialogHeader>
                            <AlertDialogBody>
                                Delete <strong>{pendingDelete?.name}</strong>? Its configuration and segment
                                assignments are removed. This cannot be undone.
                            </AlertDialogBody>
                            <AlertDialogFooter>
                                <Button ref={cancelDeleteRef} variant="outline" size="sm" onClick={() => setPendingDelete(null)}>
                                    Cancel
                                </Button>
                                <Button
                                    colorScheme="red"
                                    size="sm"
                                    ml={3}
                                    isLoading={deleting}
                                    loadingText="Deleting..."
                                    onClick={confirmDelete}
                                >
                                    Delete
                                </Button>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialogOverlay>
                </AlertDialog>
                </>
              ) : (
                <Center h="400px">
                    <Box textAlign="center">
                        <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>
                            No Access
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                            You don't have permission to view this sections.
                        </Text>
                    </Box>
                </Center>
              )}
            </BoxWrapper>
        </>
    );
};

export default RFMModels;
